import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Grid,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Build as BuildIcon,
  Dashboard as FormIcon,
  Rule as RuleIcon,
  Web as PageIcon,
  Download as DownloadIcon,
  Publish as PublishIcon,
  Preview as PreviewIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  AccountTree as WorkflowIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { App, AppManifest, AppCategory, HookName, AppHook } from '../types/apps';
import { FormDefinition, CreateFormRequest } from '../types/forms';
import { Rule, CreateRuleRequest } from '../types/rules';
import { PageDefinition, CreatePageRequest } from '../types/pages';
import { WorkflowDefinition, CreateWorkflowRequest } from '../types/workflows';
import AIAssistant from '../components/apps/AIAssistant';
import { AIResponse } from '../services/aiService';
import { appService } from '../services/appService';

// Extended AppHook interface with code field for App Builder
interface AppBuilderHook extends AppHook {
  code?: string;
  validationState?: {
    isValidating: boolean;
    isValid: boolean;
    errors: string[];
    warnings: string[];
    securityIssues: Array<{
      type: string;
      message: string;
      severity: 'low' | 'medium' | 'high' | 'critical';
      line?: number;
      column?: number;
    }>;
    lastValidated?: Date;
  };
}

interface AppBuilderState {
  forms: FormDefinition[];
  rules: Rule[];
  pages: PageDefinition[];
  workflows: WorkflowDefinition[];
  hooks: AppBuilderHook[];
  entryCode: string;
  appMetadata: {
    name: string;
    description: string;
    author: {
      name: string;
      email: string;
    };
    category: AppCategory;
    version: string;
  };
}

const sections = [
  { id: 'details', title: 'App Details', icon: BuildIcon },
  { id: 'forms', title: 'Forms', icon: FormIcon },
  { id: 'rules', title: 'Rules', icon: RuleIcon },
  { id: 'pages', title: 'Pages', icon: PageIcon },
  { id: 'workflows', title: 'Workflows', icon: WorkflowIcon },
  { id: 'hooks', title: 'Event Hooks', icon: CodeIcon },
  { id: 'entry', title: 'JavaScript Entry', icon: CodeIcon },
];

const AppBuilderPage: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    details: true, // Start with app details expanded
    entry: false,
    hooks: false,
    forms: false,
    rules: false,
    pages: false,
    workflows: false,
  });

  const renderSectionChip = (sectionId: string, appState: AppBuilderState) => {
    const getSectionData = () => {
      switch (sectionId) {
        case 'entry':
          return { count: appState.entryCode.length > 0 ? 1 : 0, label: 'entry file' };
        case 'forms':
        case 'rules':
        case 'pages':
        case 'workflows':
        case 'hooks':
          return { count: appState[sectionId].length, label: sectionId };
        default:
          return null;
      }
    };

    const sectionData = getSectionData();
    if (!sectionData) return null;

    return (
      <Chip 
        label={`${sectionData.count} ${sectionData.label}`} 
        size="small" 
        sx={{ mr: 2 }}
      />
    );
  };
  const [appState, setAppState] = useState<AppBuilderState>({
    forms: [],
    rules: [],
    pages: [],
    workflows: [],
    hooks: [],
    entryCode: '',
    appMetadata: {
      name: '',
      description: '',
      author: {
        name: '',
        email: '',
      },
      category: 'utility',
      version: '1.0.0',
    }
  });
  
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAIComponentsGenerated = (response: AIResponse) => {
    if (!response.success || !response.components) {
      showSnackbar('Failed to generate components', 'error');
      return;
    }

    const { components } = response;
    let addedCount = 0;

    // Add generated forms
    if (components.forms && components.forms.length > 0) {
      setAppState(prev => ({
        ...prev,
        forms: [...prev.forms, ...components.forms!]
      }));
      addedCount += components.forms.length;
    }

    // Add generated rules
    if (components.rules && components.rules.length > 0) {
      setAppState(prev => ({
        ...prev,
        rules: [...prev.rules, ...components.rules!]
      }));
      addedCount += components.rules.length;
    }

    // Add generated pages
    if (components.pages && components.pages.length > 0) {
      setAppState(prev => ({
        ...prev,
        pages: [...prev.pages, ...components.pages!]
      }));
      addedCount += components.pages.length;
    }

    // Add generated workflows
    if (components.workflows && components.workflows.length > 0) {
      setAppState(prev => ({
        ...prev,
        workflows: [...prev.workflows, ...components.workflows!]
      }));
      addedCount += components.workflows.length;
    }

    // Add generated hooks
    if (components.hooks && components.hooks.length > 0) {
      setAppState(prev => ({
        ...prev,
        hooks: [...prev.hooks, ...components.hooks!]
      }));
      addedCount += components.hooks.length;
    }

    // Update app metadata if provided
    if (components.appMetadata) {
      const metadata = components.appMetadata;
      setAppState(prev => ({
        ...prev,
        appMetadata: {
          ...prev.appMetadata,
          name: metadata.name || prev.appMetadata.name,
          description: metadata.description || prev.appMetadata.description,
          category: (metadata.category as AppCategory) || prev.appMetadata.category
        }
      }));
    }

    showSnackbar(`AI generated ${addedCount} components successfully!`, 'success');
    
    // Auto-expand relevant sections
    if (components.forms?.length) {
      setExpandedSections(prev => ({ ...prev, forms: true }));
    }
    if (components.rules?.length) {
      setExpandedSections(prev => ({ ...prev, rules: true }));
    }
    if (components.pages?.length) {
      setExpandedSections(prev => ({ ...prev, pages: true }));
    }
    if (components.workflows?.length) {
      setExpandedSections(prev => ({ ...prev, workflows: true }));
    }
    if (components.hooks?.length) {
      setExpandedSections(prev => ({ ...prev, hooks: true, entry: true }));
    }
  };

  const handleSectionToggle = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleMetadataChange = (field: string, value: string | AppCategory | { name: string; email: string }) => {
    setAppState(prev => ({
      ...prev,
      appMetadata: {
        ...prev.appMetadata,
        [field]: value,
      }
    }));
  };

  const handleFormsChange = (forms: FormDefinition[]) => {
    setAppState(prev => ({ ...prev, forms }));
  };

  const handleRulesChange = (rules: Rule[]) => {
    setAppState(prev => ({ ...prev, rules }));
  };

  const handlePagesChange = (pages: PageDefinition[]) => {
    setAppState(prev => ({ ...prev, pages }));
  };

  const handleWorkflowsChange = (workflows: WorkflowDefinition[]) => {
    setAppState(prev => ({ ...prev, workflows }));
  };

  const handleHooksChange = (hooks: AppBuilderHook[]) => {
    setAppState(prev => ({ ...prev, hooks }));
  };

  const handleEntryCodeChange = (entryCode: string) => {
    setAppState(prev => ({ ...prev, entryCode }));
  };

  const generateAppManifest = (): AppManifest => {
    const hooks = [];
    
    // Add hooks based on components
    if (appState.forms.length > 0) {
      hooks.push({ id: 'form-hooks', name: 'form-field-provider' as const, function: 'registerForms' });
    }
    if (appState.rules.length > 0) {
      hooks.push({ id: 'rule-hooks', name: 'rule-action-provider' as const, function: 'registerRules' });
    }
    if (appState.pages.length > 0) {
      hooks.push({ id: 'page-hooks', name: 'page-provider' as const, function: 'registerPages' });
    }
    if (appState.workflows.length > 0) {
      hooks.push({ id: 'workflow-hooks', name: 'workflow-step-provider' as const, function: 'registerWorkflows' });
    }

    // Add custom hooks
    appState.hooks.forEach(hook => {
      hooks.push({ id: hook.id, name: hook.name, function: hook.function });
    });

    return {
      // Use aggregated entry code that combines individual hook functions
      entry: generateAggregatedEntryCode(),
      dependencies: {
        // Remove React dependency since we don't use it in server-side hooks
      },
      permissions: [
        'read-forms',
        'write-forms',
        'read-rules', 
        'write-rules',
        'read-workflows',
        'write-workflows',
        'read-clients',
        'write-clients'
      ],
      hooks,
      configuration: {
        enabled: true,
        // Store the components in configuration instead of at manifest level
        components: {
          forms: appState.forms,
          rules: appState.rules,
          pages: appState.pages,
          workflows: appState.workflows,
        }
      }
    };
  };

  const generateAppDefinition = (): App => {
    const now = new Date().toISOString();
    const manifest = generateAppManifest();
    
    return {
      id: `app-${Date.now()}`,
      name: appState.appMetadata.name || 'Custom App',
      description: appState.appMetadata.description || 'Generated with App Builder',
      version: appState.appMetadata.version,
      author: appState.appMetadata.author,
      category: appState.appMetadata.category,
      isActive: true,
      isPublic: true,
      manifest,
      pricing: {
        model: 'free',
      },
      createdAt: now,
      updatedAt: now,
      publishedAt: now,
      createdBy: 'ibuild',
    };
  };

  const generateAggregatedEntryCode = (): string => {
    // If user has written custom entry code and it contains module.exports, use it as base
    if (appState.entryCode && appState.entryCode.includes('module.exports')) {
      return appState.entryCode;
    }
    
    // Otherwise, generate aggregated code from individual hook functions
    return generateDefaultEntryCode();
  };

  const generateDefaultEntryCode = (): string => {
    const lines = [
      '// Auto-generated entry file for ' + (appState.appMetadata.name || 'Custom App'),
      '// Generated by iBusiness iBuild',
      '// Compatible with JavaScript execution engine',
      '',
      'module.exports = {',
    ];

    // Add auto-generated provider functions for components
    if (appState.forms.length > 0) {
      lines.push('  // Auto-generated form provider');
      lines.push('  registerForms: function() {');
      lines.push('    return ' + JSON.stringify(appState.forms, null, 4).replace(/\n/g, '\n    ') + ';');
      lines.push('  },');
      lines.push('');
    }

    if (appState.rules.length > 0) {
      lines.push('  // Auto-generated rule provider');
      lines.push('  registerRules: function() {');
      lines.push('    return ' + JSON.stringify(appState.rules, null, 4).replace(/\n/g, '\n    ') + ';');
      lines.push('  },');
      lines.push('');
    }

    if (appState.pages.length > 0) {
      lines.push('  // Auto-generated page provider');
      lines.push('  registerPages: function() {');
      lines.push('    return ' + JSON.stringify(appState.pages, null, 4).replace(/\n/g, '\n    ') + ';');
      lines.push('  },');
      lines.push('');
    }

    if (appState.workflows.length > 0) {
      lines.push('  // Auto-generated workflow provider');
      lines.push('  registerWorkflows: function() {');
      lines.push('    return ' + JSON.stringify(appState.workflows, null, 4).replace(/\n/g, '\n    ') + ';');
      lines.push('  },');
      lines.push('');
    }

    // Add custom hook functions from individual hook code editors
    appState.hooks.forEach(hook => {
      lines.push(`  // Hook: ${hook.name} - ${hook.description}`);
      if (hook.code && hook.code.trim()) {
        // Use the actual code from the hook's code editor
        const hookCode = hook.code.trim();
        lines.push(`  ${hook.function}: ${hookCode},`);
      } else {
        // Fallback placeholder if no code is provided
        lines.push(`  ${hook.function}: function(data, context) {`);
        lines.push(`    // TODO: Implement ${hook.function} logic`);
        lines.push(`    console.log('${hook.function} called with data:', data);`);
        lines.push(`    return { processed: true, hookName: '${hook.name}' };`);
        lines.push('  },');
      }
      lines.push('');
    });

    // Close the module.exports object
    lines.push('};');
    
    return lines.join('\n');
  };

  const handleExportApp = () => {
    try {
      const appDefinition = generateAppDefinition();
      
      const exportData = {
        app: appDefinition,
        components: {
          
          hooks: appState.hooks,
        },
      };

      // Create downloadable file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${appDefinition.name.toLowerCase().replace(/\s+/g, '-')}-app.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSnackbar('App exported successfully!', 'success');
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Export error:', error);
      showSnackbar('Failed to export app', 'error');
    }
  };

  const renderSectionContent = (sectionId: string) => {
    switch (sectionId) {
      case 'details':
        return <AppDetailsStep 
          metadata={appState.appMetadata}
          onChange={handleMetadataChange}
        />;
      case 'entry':
        return <EntryCodeStep
          entryCode={appState.entryCode}
          onChange={handleEntryCodeChange}
        />;
      case 'hooks':
        return <EventHooksStep 
          hooks={appState.hooks}
          onChange={handleHooksChange}
        />;
      case 'forms':
        return <FormsStep 
          forms={appState.forms}
          onChange={handleFormsChange}
        />;
      case 'rules':
        return <RulesStep 
          rules={appState.rules}
          onChange={handleRulesChange}
        />;
      case 'pages':
        return <PagesStep 
          pages={appState.pages}
          onChange={handlePagesChange}
        />;
      case 'workflows':
        return <WorkflowsStep 
          workflows={appState.workflows}
          onChange={handleWorkflowsChange}
        />;
      case 'package':
        return <PackageStep 
          appState={appState}
          onPreview={() => setPreviewDialogOpen(true)}
          onExport={() => setExportDialogOpen(true)}
        />;
      default:
        return null;
    }
  };

  const isAppComplete = (): boolean => {
    return appState.appMetadata.name.length > 0 && appState.appMetadata.description.length > 0;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box
          component="img"
          src="/iBusiness_logo.svg"
          alt="iBusiness Logo"
          sx={{
            height: "2rem",
          }}
        />
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            component="img"
            src="/iBuild_logo_svg.svg"
            alt="iBuild Logo"
            sx={{
              height: "2rem",
            }}
          />
          <Typography variant="h6" color="text.primary" gutterBottom>
            Create powerful apps by combining forms, rules, and pages
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Build your custom application, or let iBuild AI do it, then package and export it for use in the iBusiness platform.
          </Typography>
        </CardContent>
      </Card>
      {/* AI Assistant */}
      <AIAssistant
        onComponentsGenerated={handleAIComponentsGenerated}
        appContext={{
          existingForms: appState.forms,
          existingRules: appState.rules,
          existingPages: appState.pages,
          existingWorkflows: appState.workflows,
          appCategory: appState.appMetadata.category
        }}
      />

      {/* Sections */}
      <Box sx={{ mb: 4 }}>
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <Accordion 
              key={section.id}
              expanded={expandedSections[section.id]}
              onChange={() => handleSectionToggle(section.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${section.id}-content`}
                id={`${section.id}-header`}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Icon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {section.title}
                  </Typography>
                  {renderSectionChip(section.id, appState)}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {renderSectionContent(section.id)}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Quick Export Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          onClick={() => setExportDialogOpen(true)}
          variant="contained"
          size="large"
          startIcon={<DownloadIcon />}
          disabled={!isAppComplete()}
        >
          Export App
        </Button>
      </Box>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>App Preview</DialogTitle>
        <DialogContent>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
            {JSON.stringify(generateAppDefinition(), null, 2)}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export App</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Ready to export your app? This will download a JSON file containing your app definition and all components.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            The exported file can be installed in any iBusiness instance through the App Marketplace.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExportApp} variant="contained" startIcon={<DownloadIcon />}>
            Download App
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Step Components
interface AppDetailsStepProps {
  metadata: AppBuilderState['appMetadata'];
  onChange: (field: string, value: string | AppCategory | { name: string; email: string }) => void;
}

interface EntryCodeStepProps {
  entryCode: string;
  onChange: (entryCode: string) => void;
}

const AppDetailsStep: React.FC<AppDetailsStepProps> = ({ metadata, onChange }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Provide basic information about your app
    </Typography>
    
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="App Name"
          value={metadata.name}
          onChange={(e) => onChange('name', e.target.value)}
          required
          placeholder="My Custom App"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Version"
          value={metadata.version}
          onChange={(e) => onChange('version', e.target.value)}
          placeholder="1.0.0"
        />
      </Grid>
      <Grid size={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Description"
          value={metadata.description}
          onChange={(e) => onChange('description', e.target.value)}
          required
          placeholder="Describe what your app does..."
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Author Name"
          value={metadata.author.name}
          onChange={(e) => onChange('author', { ...metadata.author, name: e.target.value })}
          placeholder="Your name or organization"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Author Email"
          type="email"
          value={metadata.author.email}
          onChange={(e) => onChange('author', { ...metadata.author, email: e.target.value })}
          placeholder="your-email@example.com"
        />
      </Grid>
      <Grid size={12}>
        <FormControl fullWidth>
          <InputLabel>Category</InputLabel>
          <Select
            value={metadata.category}
            label="Category"
            onChange={(e) => onChange('category', e.target.value)}
          >
            <MenuItem value="foundational">Foundational</MenuItem>
            <MenuItem value="utility">Utility</MenuItem>
            <MenuItem value="loan-processing">Loan Processing</MenuItem>
            <MenuItem value="integration">Integration</MenuItem>
            <MenuItem value="workflow">Workflow</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  </Box>
);

const EntryCodeStep: React.FC<EntryCodeStepProps> = ({ entryCode, onChange }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Write additional JavaScript code or override the auto-generated entry file. Individual hook functions are automatically aggregated here when exporting.
    </Typography>
    
    <Alert severity="info" sx={{ mb: 3 }}>
      <Typography variant="body2">
        <strong>Auto-Aggregation:</strong> When you export your app, individual hook functions from the Event Hooks section 
        are automatically combined into this entry file under <code>module.exports</code>. You can write additional code here 
        or completely override the auto-generated code.
      </Typography>
    </Alert>

    <Typography variant="h6" sx={{ mb: 2 }}>
      JavaScript Entry Code
    </Typography>
    
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, minHeight: '400px' }}>
      <Editor
        height="400px"
        defaultLanguage="javascript"
        value={entryCode}
        onChange={(value) => onChange(value || '')}
        options={{
          minimap: { enabled: false },
          lineNumbers: 'on',
          roundedSelection: false,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          theme: 'vs-light',
          wordWrap: 'on',
          fontSize: 14,
        }}
      />
    </Box>

    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
      <strong>Tip:</strong> Leave this empty to use auto-generated code from individual hook functions, 
      or write your complete module structure here to override auto-generation.
    </Typography>
  </Box>
);

interface FormsStepProps {
  forms: FormDefinition[];
  onChange: (forms: FormDefinition[]) => void;
}

const FormsStep: React.FC<FormsStepProps> = ({ forms, onChange }) => {
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [editingFormIndex, setEditingFormIndex] = useState<number | null>(null);

  const handleAddForm = () => {
    const newForm: FormDefinition = {
      id: `form-${Date.now()}`,
      name: `Form ${forms.length + 1}`,
      description: 'Generated form',
      version: '1.0.0',
      category: 'Other',
      fields: [],
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'ibuild',
    };
    onChange([...forms, newForm]);
  };

  const handleEditForm = (index: number) => {
    setEditingFormIndex(index);
    setShowFormBuilder(true);
  };

  const handleCreateNewForm = () => {
    setEditingFormIndex(null);
    setShowFormBuilder(true);
  };

  const handleFormBuilderSave = async (formData: CreateFormRequest) => {
    try {
      const newForm: FormDefinition = {
        id: editingFormIndex !== null ? forms[editingFormIndex].id : `form-${Date.now()}`,
        name: formData.name,
        description: formData.description || '',
        version: '1.0.0',
        category: formData.category || 'Other',
        fields: formData.fields || [],
        styling: formData.styling,
        notifications: formData.notifications,
        isActive: formData.isActive ?? true,
        createdAt: editingFormIndex !== null ? forms[editingFormIndex].createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'ibuild',
      };

      if (editingFormIndex !== null) {
        // Update existing form
        const updatedForms = [...forms];
        updatedForms[editingFormIndex] = newForm;
        onChange(updatedForms);
      } else {
        // Add new form
        onChange([...forms, newForm]);
      }

      setShowFormBuilder(false);
      setEditingFormIndex(null);
    } catch (error) {
      console.error('Error saving form:', error);
    }
  };

  const handleFormBuilderCancel = () => {
    setShowFormBuilder(false);
    setEditingFormIndex(null);
  };

  const handleRemoveForm = (formId: string) => {
    onChange(forms.filter(f => f.id !== formId));
  };

  // Import FormBuilder component dynamically to avoid circular dependencies
  const FormBuilderComponent = React.lazy(() => 
    import('../components/forms/FormBuilder').then(module => ({ default: module.default }))
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create forms that your app will provide to the platform
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddForm}
          sx={{ mr: 2 }}
        >
          Add Sample Form
        </Button>
        <Button
          variant="outlined"
          startIcon={<BuildIcon />}
          onClick={handleCreateNewForm}
        >
          Open Form Builder
        </Button>
      </Box>

      {forms.length > 0 && (
        <Grid container spacing={2}>
          {forms.map((form, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={form.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box>
                      <Typography variant="h6">{form.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {form.description}
                      </Typography>
                      <Chip label={`${form.fields?.length || 0} fields`} size="small" sx={{ mt: 1 }} />
                    </Box>
                    <Box>
                      <IconButton
                        onClick={() => handleEditForm(index)}
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleRemoveForm(form.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {forms.length === 0 && (
        <Alert severity="info">
          No forms added yet. Click "Add Sample Form" to create a sample form, or use the Form Builder to create custom forms.
        </Alert>
      )}

      {/* Form Builder Dialog */}
      <Dialog open={showFormBuilder} onClose={handleFormBuilderCancel} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingFormIndex !== null ? 'Edit Form' : 'Create New Form'}
        </DialogTitle>
        <DialogContent sx={{ height: '80vh', p: 0 }}>
          {showFormBuilder && (
            <React.Suspense fallback={<Box sx={{ p: 3 }}>Loading Form Builder...</Box>}>
              <FormBuilderComponent
                form={editingFormIndex !== null ? forms[editingFormIndex] : null}
                onSave={handleFormBuilderSave}
                onCancel={handleFormBuilderCancel}
              />
            </React.Suspense>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

interface RulesStepProps {
  rules: Rule[];
  onChange: (rules: Rule[]) => void;
}

const RulesStep: React.FC<RulesStepProps> = ({ rules, onChange }) => {
  const [showRuleBuilder, setShowRuleBuilder] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);

  const handleAddRule = () => {
    const newRule: Rule = {
      id: `rule-${Date.now()}`,
      name: `Rule ${rules.length + 1}`,
      description: 'Generated rule',
      category: 'Other',
      version: '1.0.0',
      conditions: { operator: 'AND', rules: [] },
      actions: [],
      triggers: [],
      isActive: true,
      priority: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'ibuild',
    };
    onChange([...rules, newRule]);
  };

  const handleEditRule = (index: number) => {
    setEditingRuleIndex(index);
    setShowRuleBuilder(true);
  };

  const handleCreateNewRule = () => {
    setEditingRuleIndex(null);
    setShowRuleBuilder(true);
  };

  const handleRuleBuilderSave = async (ruleData: CreateRuleRequest | Partial<CreateRuleRequest>) => {
    try {
      const newRule: Rule = {
        id: editingRuleIndex !== null ? rules[editingRuleIndex].id : `rule-${Date.now()}`,
        name: ruleData.name || 'Untitled Rule',
        description: ruleData.description || '',
        category: ruleData.category || 'Other',
        version: '1.0.0',
        conditions: ruleData.conditions || { operator: 'AND', rules: [] },
        actions: ruleData.actions || [],
        triggers: ruleData.triggers || [],
        isActive: ruleData.isActive ?? true,
        priority: ruleData.priority || 1,
        schedule: ruleData.schedule,
        tags: ruleData.tags,
        metadata: ruleData.metadata,
        createdAt: editingRuleIndex !== null ? rules[editingRuleIndex].createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'ibuild',
      };

      if (editingRuleIndex !== null) {
        // Update existing rule
        const updatedRules = [...rules];
        updatedRules[editingRuleIndex] = newRule;
        onChange(updatedRules);
      } else {
        // Add new rule
        onChange([...rules, newRule]);
      }

      setShowRuleBuilder(false);
      setEditingRuleIndex(null);
    } catch (error) {
      console.error('Error saving rule:', error);
    }
  };

  const handleRuleBuilderCancel = () => {
    setShowRuleBuilder(false);
    setEditingRuleIndex(null);
  };

  const handleRemoveRule = (ruleId: string) => {
    onChange(rules.filter(r => r.id !== ruleId));
  };

  // Import RuleBuilder component dynamically
  const RuleBuilderComponent = React.lazy(() => 
    import('../components/rules/RuleBuilder').then(module => ({ default: module.default }))
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define business rules and actions that your app will provide
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddRule}
          sx={{ mr: 2 }}
        >
          Add Sample Rule
        </Button>
        <Button
          variant="outlined"
          startIcon={<BuildIcon />}
          onClick={handleCreateNewRule}
        >
          Open Rule Builder
        </Button>
      </Box>

      {rules.length > 0 && (
        <Grid container spacing={2}>
          {rules.map((rule, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={rule.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box>
                      <Typography variant="h6">{rule.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rule.description}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip label={`${rule.conditions?.rules?.length || 0} conditions`} size="small" sx={{ mr: 1 }} />
                        <Chip label={`${rule.actions?.length || 0} actions`} size="small" />
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        onClick={() => handleEditRule(index)}
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleRemoveRule(rule.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {rules.length === 0 && (
        <Alert severity="info">
          No rules added yet. Click "Add Sample Rule" to create a sample rule, or use the Rule Builder to create custom rules.
        </Alert>
      )}

      {/* Rule Builder Dialog */}
      <Dialog open={showRuleBuilder} onClose={handleRuleBuilderCancel} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingRuleIndex !== null ? 'Edit Rule' : 'Create New Rule'}
        </DialogTitle>
        <DialogContent sx={{ height: '80vh', p: 0 }}>
          {showRuleBuilder && (
            <React.Suspense fallback={<Box sx={{ p: 3 }}>Loading Rule Builder...</Box>}>
              <RuleBuilderComponent
                rule={editingRuleIndex !== null ? rules[editingRuleIndex] : undefined}
                onSave={handleRuleBuilderSave}
                onCancel={handleRuleBuilderCancel}
              />
            </React.Suspense>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

interface PagesStepProps {
  pages: PageDefinition[];
  onChange: (pages: PageDefinition[]) => void;
}

const PagesStep: React.FC<PagesStepProps> = ({ pages, onChange }) => {
  const [showPageBuilder, setShowPageBuilder] = useState(false);
  const [editingPageIndex, setEditingPageIndex] = useState<number | null>(null);

  const handleAddPage = () => {
    const newPage: PageDefinition = {
      id: `page-${Date.now()}`,
      name: `Page ${pages.length + 1}`,
      title: `Page ${pages.length + 1}`,
      description: 'Generated page',
      version: '1.0.0',
      route: `/page-${pages.length + 1}`,
      category: 'Other',
      layout: {
        type: 'single-column',
        sections: []
      },
      permissions: { view: ['*'] },
      components: [],
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'ibuild',
    };
    onChange([...pages, newPage]);
  };

  const handleEditPage = (index: number) => {
    setEditingPageIndex(index);
    setShowPageBuilder(true);
  };

  const handleCreateNewPage = () => {
    setEditingPageIndex(null);
    setShowPageBuilder(true);
  };

  const handlePageBuilderSave = async (pageData: CreatePageRequest) => {
    try {
      const newPage: PageDefinition = {
        id: editingPageIndex !== null ? pages[editingPageIndex].id : `page-${Date.now()}`,
        name: pageData.name,
        title: pageData.title || pageData.name,
        description: pageData.description || '',
        version: '1.0.0',
        route: pageData.route,
        category: pageData.category || 'Other',
        layout: pageData.layout,
        forms: pageData.forms,
        permissions: pageData.permissions,
        components: pageData.components || [],
        meta: pageData.meta,
        styling: pageData.styling,
        isActive: true,
        createdAt: editingPageIndex !== null ? pages[editingPageIndex].createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'ibuild',
      };

      if (editingPageIndex !== null) {
        // Update existing page
        const updatedPages = [...pages];
        updatedPages[editingPageIndex] = newPage;
        onChange(updatedPages);
      } else {
        // Add new page
        onChange([...pages, newPage]);
      }

      setShowPageBuilder(false);
      setEditingPageIndex(null);
    } catch (error) {
      console.error('Error saving page:', error);
    }
  };

  const handlePageBuilderCancel = () => {
    setShowPageBuilder(false);
    setEditingPageIndex(null);
  };

  const handleRemovePage = (pageId: string) => {
    onChange(pages.filter(p => p.id !== pageId));
  };

  // Import PageBuilder component dynamically
  const PageBuilderComponent = React.lazy(() => 
    import('../components/pages/PageBuilder').then(module => ({ default: module.default }))
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Design custom pages and interfaces for your app
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPage}
          sx={{ mr: 2 }}
        >
          Add Sample Page
        </Button>
        <Button
          variant="outlined"
          startIcon={<BuildIcon />}
          onClick={handleCreateNewPage}
        >
          Open Page Builder
        </Button>
      </Box>

      {pages.length > 0 && (
        <Grid container spacing={2}>
          {pages.map((page, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={page.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box>
                      <Typography variant="h6">{page.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {page.description}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip label={`Route: ${page.route}`} size="small" sx={{ mr: 1 }} />
                        <Chip label={`${page.components?.length || 0} components`} size="small" />
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        onClick={() => handleEditPage(index)}
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleRemovePage(page.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {pages.length === 0 && (
        <Alert severity="info">
          No pages added yet. Click "Add Sample Page" to create a sample page, or use the Page Builder to create custom pages.
        </Alert>
      )}

      {/* Page Builder Dialog */}
      <Dialog open={showPageBuilder} onClose={handlePageBuilderCancel} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingPageIndex !== null ? 'Edit Page' : 'Create New Page'}
        </DialogTitle>
        <DialogContent sx={{ height: '80vh', p: 0 }}>
          {showPageBuilder && (
            <React.Suspense fallback={<Box sx={{ p: 3 }}>Loading Page Builder...</Box>}>
              <PageBuilderComponent
                page={editingPageIndex !== null ? pages[editingPageIndex] : null}
                onSave={handlePageBuilderSave}
                onCancel={handlePageBuilderCancel}
              />
            </React.Suspense>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

interface EventHooksStepProps {
  hooks: AppBuilderHook[];
  onChange: (hooks: AppBuilderHook[]) => void;
}

const EventHooksStep: React.FC<EventHooksStepProps> = ({ hooks, onChange }) => {
  const [editingHook, setEditingHook] = useState<AppBuilderHook | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  // Debounced validation function
  const validateCode = useCallback(async (hookId: string, code: string, hookType: HookName, functionName: string) => {
    // Update validation state to show loading
    const updatedHooks = hooks.map(hook => 
      hook.id === hookId 
        ? { 
            ...hook, 
            validationState: { 
              ...hook.validationState,
              isValidating: true,
              isValid: false,
              errors: [],
              warnings: [],
              securityIssues: []
            }
          }
        : hook
    );
    onChange(updatedHooks);

    try {
      const validationResult = await appService.validateJavaScriptCode(code, {
        hookType,
        functionName,
        appId: 'ibuild-preview'
      });

      // Update with validation results
      const finalHooks = hooks.map(hook => 
        hook.id === hookId 
          ? { 
              ...hook, 
              validationState: {
                isValidating: false,
                isValid: validationResult.isValid,
                errors: validationResult.errors,
                warnings: validationResult.warnings,
                securityIssues: validationResult.securityIssues,
                lastValidated: new Date()
              }
            }
          : hook
      );
      onChange(finalHooks);
    } catch (error) {
      // Update with error state
      const errorHooks = hooks.map(hook => 
        hook.id === hookId 
          ? { 
              ...hook, 
              validationState: {
                isValidating: false,
                isValid: false,
                errors: ['Validation failed: ' + (error as Error).message],
                warnings: [],
                securityIssues: [],
                lastValidated: new Date()
              }
            }
          : hook
      );
      onChange(errorHooks);
    }
  }, [hooks, onChange]);

  // Debounce the validation calls
  const [validationTimeouts, setValidationTimeouts] = useState<Record<string, NodeJS.Timeout>>({});

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(validationTimeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [validationTimeouts]);

  const debouncedValidateCode = useCallback((hookId: string, code: string, hookType: HookName, functionName: string) => {
    // Clear existing timeout
    if (validationTimeouts[hookId]) {
      clearTimeout(validationTimeouts[hookId]);
    }

    // Set new timeout
    const timeout = setTimeout(() => {
      validateCode(hookId, code, hookType, functionName);
    }, 1000); // 1 second delay

    setValidationTimeouts(prev => ({
      ...prev,
      [hookId]: timeout
    }));
  }, [validateCode, validationTimeouts]);

  const hookTypes: { value: HookName; label: string; description: string }[] = [
    { value: 'form-field-provider', label: 'Form Field Provider', description: 'Provides custom form fields' },
    { value: 'rule-action-provider', label: 'Rule Action Provider', description: 'Provides custom rule actions' },
    { value: 'workflow-step-provider', label: 'Workflow Step Provider', description: 'Provides custom workflow steps' },
    { value: 'validation-provider', label: 'Validation Provider', description: 'Provides custom validation functions' },
    { value: 'page-provider', label: 'Page Provider', description: 'Provides custom pages' },
    { value: 'page-mount', label: 'Page Mount', description: 'Executes when a page is mounted' },
    { value: 'page-unmount', label: 'Page Unmount', description: 'Executes when a page is unmounted' },
    { value: 'form-submitted', label: 'Form Submitted', description: 'Executes when a form is submitted' },
    { value: 'rule-executed', label: 'Rule Executed', description: 'Executes when a rule is triggered' },
    { value: 'workflow-started', label: 'Workflow Started', description: 'Executes when a workflow starts' },
    { value: 'workflow-completed', label: 'Workflow Completed', description: 'Executes when a workflow completes' },
    { value: 'client-created', label: 'Client Created', description: 'Executes when a client is created' },
    { value: 'loan-created', label: 'Loan Created', description: 'Executes when a loan is created' },
    { value: 'email-sent', label: 'Email Sent', description: 'Executes when an email is sent' },
    { value: 'email-opened', label: 'Email Opened', description: 'Executes when an email is opened' },
    { value: 'email-clicked', label: 'Email Clicked', description: 'Executes when an email link is clicked' },
    { value: 'email-bounced', label: 'Email Bounced', description: 'Executes when an email bounces' },
    { value: 'email-failed', label: 'Email Failed', description: 'Executes when an email fails to send' },
  ];

  const getDefaultCode = (hookName: HookName): string => {
    switch (hookName) {
      case 'form-field-provider':
      case 'rule-action-provider':
      case 'workflow-step-provider':
      case 'validation-provider':
      case 'page-provider':
        return `function() {\n  // Return an array of your custom ${hookName.split('-')[0]}s\n  return [];\n}`;
      case 'page-mount':
      case 'page-unmount':
        return `function(pageContext, context) {\n  // Handle page ${hookName.split('-')[1]} event\n  console.log('Page ${hookName.split('-')[1]}:', pageContext);\n}`;
      case 'form-submitted':
        return `function(formData, context) {\n  // Handle form submission\n  console.log('Form submitted:', formData);\n  return { processed: true, formId: formData.formId };\n}`;
      case 'rule-executed':
        return `function(ruleResult, context) {\n  // Handle rule execution\n  console.log('Rule executed:', ruleResult);\n  return { processed: true };\n}`;
      case 'workflow-started':
      case 'workflow-completed':
        return `function(workflowData, context) {\n  // Handle workflow ${hookName.split('-')[1]} event\n  console.log('Workflow ${hookName.split('-')[1]}:', workflowData);\n  return { processed: true };\n}`;
      case 'client-created':
        return `function(clientData, context) {\n  // Handle client creation\n  console.log('Client created:', clientData);\n  return { processed: true, clientId: clientData.clientId };\n}`;
      case 'loan-created':
        return `function(loanData, context) {\n  // Handle loan creation\n  console.log('Loan created:', loanData);\n  return { processed: true, loanId: loanData.loanId };\n}`;
      default:
        return `function(eventData, context) {\n  // Handle ${hookName.replace('-', ' ')} event\n  console.log('Event triggered:', eventData);\n  return { processed: true };\n}`;
    }
  };

  const handleAddHook = () => {
    const newHook: AppBuilderHook = {
      id: `hook-${Date.now()}`,
      name: 'page-mount',
      function: `handlePageMount${Date.now()}`,
      description: 'New event hook',
      code: getDefaultCode('page-mount'),
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setEditingHook(newHook);
    setIsEditorOpen(true);
  };

  const handleEditHook = (hook: AppBuilderHook) => {
    setEditingHook({ ...hook });
    setIsEditorOpen(true);
  };

  const handleSaveHook = () => {
    if (!editingHook) return;

    const existingIndex = hooks.findIndex(h => h.id === editingHook.id);
    if (existingIndex >= 0) {
      // Update existing hook
      const updatedHooks = [...hooks];
      updatedHooks[existingIndex] = { ...editingHook, updatedAt: new Date().toISOString() };
      onChange(updatedHooks);
    } else {
      // Add new hook
      onChange([...hooks, editingHook]);
    }

    setIsEditorOpen(false);
    setEditingHook(null);
  };

  const handleRemoveHook = (hookId: string) => {
    onChange(hooks.filter(h => h.id !== hookId));
  };

  const handleHookChange = (field: keyof AppBuilderHook, value: string | HookName) => {
    if (!editingHook) return;

    const updatedHook = { ...editingHook, [field]: value };
    
    // Update function name and default code when hook name changes
    if (field === 'name') {
      const functionName = `handle${value.split('-').map((word: string) => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join('')}${Date.now()}`;
      updatedHook.function = functionName;
      
      // Update default code when hook type changes
      updatedHook.code = getDefaultCode(value as HookName);
    }

    setEditingHook(updatedHook);
  };

  const handleCodeChange = (hookId: string, newCode: string) => {
    const updatedHooks = hooks.map(hook => 
      hook.id === hookId ? { ...hook, code: newCode, updatedAt: new Date().toISOString() } : hook
    );
    onChange(updatedHooks);

    // Trigger validation with debounce
    const hook = hooks.find(h => h.id === hookId);
    if (hook && newCode.trim()) {
      debouncedValidateCode(hookId, newCode, hook.name, hook.function);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create custom event hooks with individual JavaScript functions. Each hook function will be automatically aggregated into the entry file when publishing.
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddHook}
          sx={{ mr: 2 }}
        >
          Add Event Hook
        </Button>
      </Box>

      {hooks.length > 0 && (
        <Grid container spacing={2}>
          {hooks.map((hook) => (
            <Grid size={12} key={hook.id}>
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`hook-${hook.id}-content`}
                  id={`hook-${hook.id}-header`}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mr: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">{hook.function}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {hookTypes.find(t => t.value === hook.name)?.label} - {hook.description}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip 
                          label={hook.name} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                        <Typography variant="body2" color="text.secondary">
                          → function: <code>{hook.function}</code>
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Validation Status Indicator */}
                      {hook.validationState && (
                        <Tooltip title={
                          hook.validationState.isValidating 
                            ? 'Validating code...'
                            : hook.validationState.isValid 
                              ? 'Code is valid'
                              : `${hook.validationState.errors.length} errors, ${hook.validationState.securityIssues.length} security issues`
                        }>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {hook.validationState.isValidating ? (
                              <CircularProgress size={16} />
                            ) : hook.validationState.isValid ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : hook.validationState.securityIssues.some(issue => issue.severity === 'critical' || issue.severity === 'high') ? (
                              <SecurityIcon color="error" fontSize="small" />
                            ) : (
                              <ErrorIcon color="error" fontSize="small" />
                            )}
                          </Box>
                        </Tooltip>
                      )}
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditHook(hook);
                        }}
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveHook(hook.id);
                        }}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      JavaScript function code for <strong>{hook.function}</strong>:
                    </Typography>
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, minHeight: '200px' }}>
                      <Editor
                        height="200px"
                        defaultLanguage="javascript"
                        value={hook.code || getDefaultCode(hook.name)}
                        onChange={(value) => handleCodeChange(hook.id, value || '')}
                        options={{
                          minimap: { enabled: false },
                          lineNumbers: 'on',
                          roundedSelection: false,
                          scrollBeyondLastLine: false,
                          automaticLayout: true,
                          theme: 'vs-light',
                          wordWrap: 'on',
                          fontSize: 12,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      This function will be exported as <code>{hook.function}</code> in the final entry file.
                    </Typography>
                    
                    {/* Validation Feedback */}
                    {hook.validationState && !hook.validationState.isValidating && (
                      <Box sx={{ mt: 2 }}>
                        {/* Errors */}
                        {hook.validationState.errors.length > 0 && (
                          <Alert severity="error" sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              Syntax Errors:
                            </Typography>
                            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                              {hook.validationState.errors.map((error, index) => (
                                <li key={index}>
                                  <Typography variant="body2">{error}</Typography>
                                </li>
                              ))}
                            </ul>
                          </Alert>
                        )}

                        {/* Security Issues */}
                        {hook.validationState.securityIssues.length > 0 && (
                          <Alert 
                            severity={hook.validationState.securityIssues.some(issue => issue.severity === 'critical' || issue.severity === 'high') ? 'error' : 'warning'} 
                            sx={{ mb: 1 }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              Security Issues:
                            </Typography>
                            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                              {hook.validationState.securityIssues.map((issue, index) => (
                                <li key={index}>
                                  <Typography variant="body2">
                                    <strong>{issue.severity.toUpperCase()}:</strong> {issue.message}
                                    {issue.line && ` (line ${issue.line})`}
                                  </Typography>
                                </li>
                              ))}
                            </ul>
                          </Alert>
                        )}

                        {/* Warnings */}
                        {hook.validationState.warnings.length > 0 && (
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              Warnings:
                            </Typography>
                            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                              {hook.validationState.warnings.map((warning, index) => (
                                <li key={index}>
                                  <Typography variant="body2">{warning}</Typography>
                                </li>
                              ))}
                            </ul>
                          </Alert>
                        )}

                        {/* Success Message */}
                        {hook.validationState.isValid && (
                          <Alert severity="success">
                            <Typography variant="body2">
                              ✅ Code is valid and secure!
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      )}

      {hooks.length === 0 && (
        <Alert severity="info">
          No event hooks added yet. Click "Add Event Hook" to create custom event handlers with individual code editors.
        </Alert>
      )}

      {/* Hook Editor Dialog for hook metadata */}
      <Dialog 
        open={isEditorOpen} 
        onClose={() => setIsEditorOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editingHook && hooks.find(h => h.id === editingHook.id) ? 'Edit Event Hook' : 'Create Event Hook'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
          {editingHook && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Configure the hook metadata. The function code will be editable in the main interface after saving.
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Hook Type</InputLabel>
                    <Select
                      value={editingHook.name}
                      label="Hook Type"
                      onChange={(e) => handleHookChange('name', e.target.value as HookName)}
                    >
                      {hookTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box>
                            <Typography variant="body1">{type.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {type.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Function Name"
                    value={editingHook.function}
                    onChange={(e) => handleHookChange('function', e.target.value)}
                    placeholder="myCustomFunction"
                    helperText="This will be the exported function name"
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={editingHook.description}
                    onChange={(e) => handleHookChange('description', e.target.value)}
                    placeholder="Describe what this hook does..."
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Example function signature for {editingHook.name}:</strong>
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1 }}>
                  {editingHook.name === 'form-submitted' && 'function(formData, context) { ... }'}
                  {editingHook.name === 'client-created' && 'function(clientData, context) { ... }'}
                  {editingHook.name === 'page-mount' && 'function(pageContext, context) { ... }'}
                  {editingHook.name === 'form-field-provider' && 'function(props, context) { ... }'}
                  {!['form-submitted', 'client-created', 'page-mount', 'form-field-provider'].includes(editingHook.name) && 'function(eventData, context) { ... }'}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditorOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveHook} variant="contained">
            Save Hook
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface PackageStepProps {
  appState: AppBuilderState;
  onPreview: () => void;
  onExport: () => void;
}

interface WorkflowsStepProps {
  workflows: WorkflowDefinition[];
  onChange: (workflows: WorkflowDefinition[]) => void;
}

const WorkflowsStep: React.FC<WorkflowsStepProps> = ({ workflows, onChange }) => {
  const [showWorkflowBuilder, setShowWorkflowBuilder] = useState(false);
  const [editingWorkflowIndex, setEditingWorkflowIndex] = useState<number | null>(null);

  const handleAddWorkflow = () => {
    const newWorkflow: WorkflowDefinition = {
      id: `workflow-${Date.now()}`,
      name: `Workflow ${workflows.length + 1}`,
      description: 'Generated workflow',
      version: '1.0.0',
      category: 'Other',
      steps: [],
      connections: [],
      triggers: [],
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'ibuild',
    };
    onChange([...workflows, newWorkflow]);
  };

  const handleEditWorkflow = (index: number) => {
    setEditingWorkflowIndex(index);
    setShowWorkflowBuilder(true);
  };

  const handleCreateNewWorkflow = () => {
    setEditingWorkflowIndex(null);
    setShowWorkflowBuilder(true);
  };

  const handleWorkflowBuilderSave = async (workflowData: Partial<CreateWorkflowRequest & { id?: string; version?: string }>) => {
    try {
      const newWorkflow: WorkflowDefinition = {
        id: workflowData.id || (editingWorkflowIndex !== null ? workflows[editingWorkflowIndex].id : `workflow-${Date.now()}`),
        name: workflowData.name || 'Untitled Workflow',
        description: workflowData.description || '',
        version: workflowData.version || '1.0.0',
        category: workflowData.category || 'Other',
        steps: workflowData.steps || [],
        connections: workflowData.connections || [],
        variables: workflowData.variables || [],
        triggers: workflowData.triggers || [],
        isActive: true,
        createdAt: editingWorkflowIndex !== null ? workflows[editingWorkflowIndex].createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'ibuild',
      };

      if (editingWorkflowIndex !== null) {
        // Update existing workflow
        const updatedWorkflows = [...workflows];
        updatedWorkflows[editingWorkflowIndex] = newWorkflow;
        onChange(updatedWorkflows);
      } else {
        // Add new workflow
        onChange([...workflows, newWorkflow]);
      }

      setShowWorkflowBuilder(false);
      setEditingWorkflowIndex(null);
    } catch (error) {
      console.error('Error saving workflow:', error);
    }
  };

  const handleWorkflowBuilderCancel = () => {
    setShowWorkflowBuilder(false);
    setEditingWorkflowIndex(null);
  };

  const handleRemoveWorkflow = (workflowId: string) => {
    onChange(workflows.filter(w => w.id !== workflowId));
  };

  // Import WorkflowBuilder component dynamically
  const WorkflowBuilderComponent = React.lazy(() => 
    import('../components/workflow/WorkflowBuilder').then(module => ({ default: module.default }))
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create multi-step business processes that your app will provide
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddWorkflow}
          sx={{ mr: 2 }}
        >
          Add Sample Workflow
        </Button>
        <Button
          variant="outlined"
          startIcon={<BuildIcon />}
          onClick={handleCreateNewWorkflow}
        >
          Open Workflow Builder
        </Button>
      </Box>

      {workflows.length > 0 && (
        <Grid container spacing={2}>
          {workflows.map((workflow, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={workflow.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box>
                      <Typography variant="h6">{workflow.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {workflow.description}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip label={`${workflow.steps?.length || 0} steps`} size="small" sx={{ mr: 1 }} />
                        <Chip label={`${workflow.triggers?.length || 0} triggers`} size="small" />
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        onClick={() => handleEditWorkflow(index)}
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleRemoveWorkflow(workflow.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {workflows.length === 0 && (
        <Alert severity="info">
          No workflows added yet. Click "Add Sample Workflow" to create a sample workflow, or use the Workflow Builder to create custom workflows.
        </Alert>
      )}

      {/* Workflow Builder Dialog */}
      <Dialog open={showWorkflowBuilder} onClose={handleWorkflowBuilderCancel} maxWidth="xl" fullWidth>
        <DialogContent sx={{ height: '90vh', p: 0 }}>
          {showWorkflowBuilder && (
            <React.Suspense fallback={<Box sx={{ p: 3 }}>Loading Workflow Builder...</Box>}>
              <WorkflowBuilderComponent
                workflowId={editingWorkflowIndex !== null ? workflows[editingWorkflowIndex].id : undefined}
                initialWorkflow={editingWorkflowIndex !== null ? workflows[editingWorkflowIndex] : undefined}
                onSave={handleWorkflowBuilderSave}
                onCancel={handleWorkflowBuilderCancel}
                onTest={(workflow) => console.log('Testing workflow in iBuild:', workflow)}
              />
            </React.Suspense>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const PackageStep: React.FC<PackageStepProps> = ({ appState, onPreview, onExport }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Review your app and export it for installation
    </Typography>

    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              App Summary
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {appState.appMetadata.name || 'Untitled App'}
            </Typography>
            <Typography variant="body2">
              <strong>Description:</strong> {appState.appMetadata.description || 'No description'}
            </Typography>
            <Typography variant="body2">
              <strong>Version:</strong> {appState.appMetadata.version}
            </Typography>
            <Typography variant="body2">
              <strong>Author:</strong> {appState.appMetadata.author.name || 'Unknown'}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2">
              <strong>Components:</strong>
            </Typography>
            <Typography variant="body2">
              • {appState.forms.length} forms
            </Typography>
            <Typography variant="body2">
              • {appState.rules.length} rules
            </Typography>
            <Typography variant="body2">
              • {appState.pages.length} pages
            </Typography>
            <Typography variant="body2">
              • {appState.workflows.length} workflows
            </Typography>
            <Typography variant="body2">
              • {appState.hooks.length} event hooks
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Export Options
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={onPreview}
                fullWidth
              >
                Preview App Definition
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={onExport}
                fullWidth
              >
                Download App Package
              </Button>
              <Button
                variant="outlined"
                startIcon={<PublishIcon />}
                disabled
                fullWidth
              >
                Publish to Marketplace (Coming Soon)
              </Button>
            </Box>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              The exported app can be imported into any iBusiness instance through the App Marketplace.
            </Alert>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Box>
);

export default AppBuilderPage;
